import { useMsal } from "@azure/msal-react";
import {
    Checkbox,
    DefaultButton,
    Link,
    Panel,
    PanelType,
    PrimaryButton,
    Separator,
    Spinner,
    SpinnerSize,
    Stack,
    Text
} from "@fluentui/react";
import { RootState } from "app/store";
import Excel from 'assets/SVGPack/excel.svg';
import { EControlType } from "components/ControlWrapper";
import CustomPanel from "components/controls/CustomPanel";
import FileImportDialog from "components/controls/FileImportDialog";
import { EValidationSeverity, IValidationMessage } from "components/controls/ValidationView/ValidationView.type";
import { saveAs } from 'file-saver';
import { CalculateRunOutClaimsEndDate, CancellationFieldsByCategory, createGroupNumber, emitCustomEvent, EViewType, fadeIn, friendlyValue, friendlyValueToFlexibleFieldValue, panelButtonsStyles } from "global/Application";
import { useApplication } from "global/Application/Context";
import { AppDataSource } from "global/DataSources/AppDataSource";
import { MsalTokenSource } from "global/DataSources/MsalTokenSource";
import { LegacyGroupStructureExcelExtract, GroupStructureExcelExtract } from "global/ExcelExtract";
import { addUpdateGroupStructure } from "global/Reducer/dataSlice";
import { setPendingExport } from "global/Reducer/statusSlice";
import { ArrayToArrayByPredicateFlat, ArrayToMapByPredicateWithMap, cyrb53, Delay, formatDate, parseDate } from "global/Utility/TSFunctions";
import { applyVisibilityGroupNumbers, BuildVisibilityScope } from "global/Visibility/GlobalVisibility";
import { IFieldReference } from "global/Visibility/IFieldReference";
import { IVisibilityScope } from "global/Visibility/IVisibilityScope.type";
import {
    contact as IXrmTypes_contact
} from 'model/CRM';
import {
    EBenefitStatus as FAM_EBenefitStatus,
    ICESNumber as FAM_ICESNumber,
    IDepartment as FAM_IDepartment,
    IEntityReferenceValue as FAM_IEntityReferenceValue,
    IGroupNumber as FAM_IGroupNumber,
    IRef as FAM_IRef
} from "model/FlexibleAttributeModel";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from 'uuid';
import ValidationDialog from "views/BlueRelationship/ValidationDialog";
import { EValidationState, IValidationModel } from "views/BlueRelationship/ValidationDialog/ValidationDialog.type";
import ValidationItem from "views/BlueRelationship/ValidationDialog/ValidationItem";
import { EvaluateFieldDisability } from "views/BlueRelationship/FlexibleFieldView";
import { EView } from "../EView.type";
import { IGroupStructureImportWizardProps } from "./IGroupStructureImportWizardProps.type";
import { $att_$group_number_$bcbs_cancellationeffectivedate_id, $att_$group_number_$bcbs_contactid_id, $att_$group_number_$bcbs_groupleaderid_id } from "model/ConfigAttributeConstants";
import { $cat_$group_number_id } from "model/ConfigCategoryConstants";
import { $matt_$group_number_$bcbs_cancellationeffectivedate_id } from "model/MasterAttributeConstants";

// interface IFeedback {
//     status: EStatus;
//     messages?: [];
// }


interface IApi {
    '$type': "BCBSSC.Azure.Portal.Models.RequestBatch, BCBSSC.Azure.Portal" |
    "BCBSSC.Azure.Portal.Models.ReadSheetRequest, BCBSSC.Azure.Portal" |
    "System.Collections.Generic.Dictionary`2[[System.String, System.Private.CoreLib],[System.String, System.Private.CoreLib]], System.Private.CoreLib" |
    "BCBSSC.Azure.Portal.Models.UpdateTableSheetRequest, BCBSSC.Azure.Portal" |
    null

}

interface IRowApi extends IApi {
    [key: string]: string;
    '$type': "System.Collections.Generic.Dictionary`2[[System.String, System.Private.CoreLib],[System.String, System.Private.CoreLib]], System.Private.CoreLib";
}

interface IReadSheetRequestApi extends IApi {
    '$type': "BCBSSC.Azure.Portal.Models.ReadSheetRequest, BCBSSC.Azure.Portal";
    'comment': string;
    'IsFirstRowHeader': boolean;
    'sheetName': string;
    'rows': IRowApi[];
}

// interface IUpdateSheetRequestApi extends IApi {
//     '$type': "BCBSSC.Azure.Portal.Models.UpdateTableSheetRequest, BCBSSC.Azure.Portal",
//     TableName: string;
//     SheetName: string;
//     IsFirstRowHeader: true,
//     comment: string | null;
//     'Rows': IRowApi[];
// }

interface IRequestBatchApi {
    '$type': "BCBSSC.Azure.Portal.Models.RequestBatch, BCBSSC.Azure.Portal";
    fileBase64: string;
    requests: IReadSheetRequestApi[]
}




enum EViewState {
    export, import, results
}


export interface TLog {
    date: Date;
    type: 'warn' | 'error' | 'info' | 'ok';
    row: number | null;
    message: string;
}


type TValidationModel = IValidationModel<FAM_IGroupNumber[]>;

interface IValidatedTransaction {
    validation?: TValidationModel
}

// interface IValidationData {

// }


interface ISaveTransaction extends IValidatedTransaction {
    groupNumbers: FAM_IGroupNumber[],
    cesNumbers: FAM_ICESNumber[],
    departments: FAM_IDepartment[],
    metaData: Record<string, string>;
    logs: IValidationMessage[],
}

// Template ID moved to GroupStructureExcelExtract.ts
// See GROUP_STRUCTURE_TEMPLATE_ID export

interface IViewModel {
    view: EViewState,
    importReady: boolean,
    exportReady: boolean,
    importGroupNumbers: boolean,
    importCESClientNumbers: boolean,
    importDepartments: boolean,
    exportDeactivated: boolean,
    hideInfoMessages: boolean,
    processing: boolean,
    useLegacyExport: boolean,
}

const GroupStructureImportWizard: React.FunctionComponent<IGroupStructureImportWizardProps> = (props) => {

    const { instance } = useMsal();
    const application = useApplication();
    const state = useSelector((state: RootState) => state);

    const dispatch = useDispatch();

    const [updateTransaction, setUpdateTransaction] = useState<ISaveTransaction | null>(null);
    const [contactsByAccountId, setContactsByAccountId] = useState<IXrmTypes_contact[] | null>(null);

    // upload
    const [fileUpload, setFileUpload] = useState<string | null>(null);

    const clearCommandText = '(clear)';

    const [viewModel, setViewModel] = useState<IViewModel>({
        view: props.variant === 'export' || !application.ChangeTypePermissions.groupStructure.import ? EViewState.export : EViewState.import,
        importReady: false,
        exportReady: false,
        importGroupNumbers: true,
        importCESClientNumbers: true,
        importDepartments: true,
        exportDeactivated: false,
        hideInfoMessages: false,
        processing: false,
        useLegacyExport: false,
    });

    const tokenSource = new MsalTokenSource(instance);
    const dataSource = new AppDataSource(tokenSource);

    const [prompts, setPrompts] = useState<IValidationMessage[]>([]);


    useEffect(() => {
        (async () => {
            const prompts: IValidationMessage[] = [];
            try {

                // CES Client Numbers
                // ---------------------------------------------
                {
                    // checking unique names, this is to ensure after export users would be able to pick from spreadsheet
                    const CESNumbers = ArrayToArrayByPredicateFlat(state.data.json.groupStructure.cesNumbers, t => t.cesNumber)
                        .filter(t => t.length > 1);

                    if (CESNumbers.length > 0)
                        CESNumbers.forEach(eachCESClientNumber => {
                            prompts.push({
                                title: `CES Client Number name (${eachCESClientNumber[0].cesNumber}) is not unique.`,
                                description: `The CES Client Numbers must be unique. The import function utilizes these distinct names to accurately map to the corresponding plans.\nYou can use Manage CES Client number to reconcile these CES numbers.`,
                                type: EValidationSeverity.warn,
                            })
                        })
                }


                // Plans
                // ---------------------------------------------
                {
                    // checking unique names, this is to ensure after export users would be able to pick from spreadsheet
                    const plans = ArrayToArrayByPredicateFlat(state.data.json.plans, t => t.name)
                        .filter(t => t.length > 1);

                    if (plans.length > 0)
                        plans.forEach(eachPlan => {
                            prompts.push({
                                title: `Plan name (${eachPlan[0].name}) is not unique.`,
                                description: `The names assigned to plans must be unique. The import function utilizes these distinct names to accurately map to the corresponding plans.`,
                                type: EValidationSeverity.warn,
                            })
                        })
                }

                // Accounts Receivables
                // ---------------------------------------------
                {
                    // checking unique names, this is to ensure after export users would be able to pick from spreadsheet

                    const accountsReceivables = ArrayToArrayByPredicateFlat(state.data.json.claimsARs, t => t.name)
                        .filter(t => t.length > 1);


                    if (accountsReceivables.length > 0)
                        accountsReceivables.forEach(eachAccountsReceivables => {
                            prompts.push({
                                title: `Accounts Receivable name (${eachAccountsReceivables[0].name}) is not unique.`,
                                description: `The names assigned to Accounts Receivable must be unique. The import function utilizes these distinct names to accurately map to the corresponding plans.`,
                                type: EValidationSeverity.warn,
                            })
                        })
                }

                // Stop Loss
                // ---------------------------------------------
                {
                    // checking unique names, this is to ensure after export users would be able to pick from spreadsheet
                    const stopLosses = ArrayToArrayByPredicateFlat(state.data.json.stopLoss, t => t.name)
                        .filter(t => t.length > 1);

                    if (stopLosses.length > 0)
                        stopLosses.forEach(eachStopLoss => {
                            prompts.push({
                                title: `Stop Loss name (${eachStopLoss[0].name}) is not unique.`,
                                description: `The names assigned to Stop Loss must be unique. The import function utilizes these distinct names to accurately map to the corresponding plans.`,
                                type: EValidationSeverity.warn,
                            })
                        })
                }

                // Contacts
                // ---------------------------------------------
                {
                    // loading contacts for export
                    const contactsByAccountId = await dataSource.contact_byAccountId_getAsync(state.data.snapshot?._bcbs_groupid_value!, ['fullname', 'emailaddress1']);
                    setContactsByAccountId(contactsByAccountId);

                    // checking unique names, this is to ensure after export users would be able to pick
                    const contactNames = ArrayToArrayByPredicateFlat(contactsByAccountId, t => t.fullname!)
                        .filter(t => t.length > 1);

                    if (contactNames.length > 0)
                        contactNames.forEach(eachContact => {
                            prompts.push({
                                title: `Contact (${eachContact[0].fullname}) is not unique.`,
                                description: `The names assigned to contact must be unique. The import function utilizes these distinct names to accurately map to the corresponding contact.`,
                                type: EValidationSeverity.warn,
                            })
                        })
                }

                // get errors first
                prompts.sort((b, a) => { return b.type - a.type });

                setPrompts(t => ([...t, ...prompts]));

                setViewModel(vm => ({
                    ...vm,
                    exportReady: true,
                    importReady: true,
                }));

            } catch (error: any) {
                emitCustomEvent({
                    eventType: 'globalException',
                    details: error
                });
            }
            finally {
            }

        })();

    }, []);


    const onRenderImportExport = () => {

        return <Stack >
            <Separator />

            <Stack horizontal tokens={{ childrenGap: 15 }} styles={{ root: { marginBottom: '15px' } }}>
                <img src={Excel} style={{ width: '60px' }} alt={"application icon"} />
                <Stack tokens={{ childrenGap: 5 }}>
                    <Text block variant="small" >
                        Download the Group Structure data (CES Client Numbers, Group Numbers, Departments, Plans, Accounts Receivables, and Stop Losses) as an Excel file.
                    </Text>
                    <Text block variant="small" >
                        Make any necessary changes in the file and upload it to apply updates.
                    </Text>
                </Stack>
            </Stack>


            {viewModel.view === EViewState.export &&
                <CustomPanel
                    title="Advanced Settings"
                    canCollapse={true}
                    defaultCollapsed={true}
                >
                    <Stack
                        tokens={{ childrenGap: 8 }}
                        styles={{ root: { marginTop: '15px', marginBottom: '10px' } }}
                    >
                        <Checkbox
                            label="Use Legacy Export (compatible with import)"
                            checked={viewModel.useLegacyExport}
                            onChange={(event, checked) => { setViewModel(t => ({ ...t, useLegacyExport: checked ?? true })) }}
                        />
                        <Text variant="small" styles={{ root: { marginLeft: '26px', color: '#666' } }}>
                            {viewModel.useLegacyExport 
                                ? "Legacy export uses a template-based format that can be re-imported."
                                : "Modern export includes dropdown validations but cannot be re-imported."}
                        </Text>
                        <Checkbox
                            label="Process Group Numbers"
                            disabled={true}
                            checked={viewModel.importGroupNumbers}
                            onChange={(event, checked) => { setViewModel(t => ({ ...t, importGroupNumbers: checked ?? false })) }}
                        />
                        <Checkbox
                            label="Process CES Client Numbers"
                            disabled={true}
                            checked={viewModel.importCESClientNumbers}
                            onChange={(event, checked) => { setViewModel(t => ({ ...t, importCESClientNumbers: checked ?? false })) }}
                        />
                        <Checkbox
                            label="Process Departments"
                            disabled={true}
                            checked={viewModel.importDepartments}
                            onChange={(event, checked) => { setViewModel(t => ({ ...t, importDepartments: checked ?? false })) }}
                        />

                        <Checkbox
                            label="Exclude deactivated"
                            disabled={true}
                            checked={!viewModel.exportDeactivated}
                            onChange={(event, checked) => { setViewModel(t => ({ ...t, exportDeactivated: checked ?? false })) }}
                        />
                    </Stack>
                </CustomPanel>

            }

            {viewModel.view === EViewState.import &&
                application.ChangeTypePermissions.groupStructure.import &&
                !state.status.gsImportPendingExport &&
                (
                    <Stack>
                        <CustomPanel
                            title="Advanced Settings"
                            canCollapse={true}
                            defaultCollapsed={true}
                        >
                            <Stack
                                tokens={{ childrenGap: 8 }}
                                styles={{ root: { marginTop: '15px', marginBottom: '10px' } }}
                            >
                                <Checkbox
                                    label="Process Group Numbers"
                                    disabled={true}
                                    checked={viewModel.importGroupNumbers}
                                    onChange={(event, checked) => { setViewModel(t => ({ ...t, importGroupNumbers: checked ?? false })) }}
                                />
                                <Checkbox
                                    label="Process CES Client Numbers"
                                    disabled={true}
                                    checked={viewModel.importCESClientNumbers}
                                    onChange={(event, checked) => { setViewModel(t => ({ ...t, importCESClientNumbers: checked ?? false })) }}
                                />
                                <Checkbox
                                    label="Process Departments"
                                    disabled={true}
                                    checked={viewModel.importDepartments}
                                    onChange={(event, checked) => { setViewModel(t => ({ ...t, importDepartments: checked ?? false })) }}
                                />
                            </Stack>
                        </CustomPanel>

                        <CustomPanel
                            title="Import Document"
                            canCollapse={true}
                            defaultCollapsed={false}
                        >

                            <FileImportDialog
                                maxFiles={1}
                                showAttached={true}
                                onDropBase64={(file) => {
                                    setFileUpload(file);
                                }}
                            />
                        </CustomPanel>

                    </Stack>
                )
            }

            {viewModel.view === EViewState.import && !application.ChangeTypePermissions.groupStructure.import &&
                <Text style={{ color: 'red', margin: '10px' }}>Import is disabled for Administrative Change configurations.</Text>
            }

            {viewModel.view === EViewState.import && state.status.gsImportPendingExport &&
                <Text style={{ color: 'red', margin: '10px' }}>Import is disabled until the pending export is completed.</Text>
            }


            {viewModel.processing &&
                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 10 }} styles={{ root: { paddingBottom: '10px' } }}>
                    <Spinner size={SpinnerSize.medium} />
                    <Text>Processing</Text>
                </Stack>
            }

            {prompts.length > 0 &&
                <CustomPanel
                    title="Logs"
                    defaultCollapsed={false}
                    canCollapse={true}
                >
                    {onRenderResults()}
                </CustomPanel>
            }

        </Stack>;
    }


    const onRenderExportFooter = () => {
        return <div className={fadeIn}>
            <PrimaryButton text={props.isReport ? 'Download Report' : 'Download'}
                styles={panelButtonsStyles}
                disabled={!viewModel.exportReady || viewModel.processing}
                onClick={async () => {

                    setViewModel(vm => ({
                        ...vm,
                        processing: true,
                    }));

                    // queue so the UI can update
                    await Delay(0);

                    try {
                        const fileName = `${state.data.json.bcbs_shortname ?? state.data.snapshot?.bcbs_groupconfigurationsnapshotid}-${props.isReport ? 'report' : 'gs'}-${new Date().getTime()}.xlsx`;
                        
                        if (viewModel.useLegacyExport) {
                            // Legacy export - template-based, compatible with import
                            const { templateBlob, logs } = await LegacyGroupStructureExcelExtract({
                                state,
                                dataSource,
                                contactsByAccountId
                            });

                            saveAs(templateBlob, fileName);
                            setPrompts(t => ([...t, ...logs]));
                        } else {
                            // Modern export - with dropdown validations
                            const blob = await GroupStructureExcelExtract({
                                state,
                                dataSource,
                                contactsByAccountId,
                                isReport: props.isReport ?? false,
                            });

                            saveAs(blob, fileName);
                            setPrompts(t => ([...t, {
                                date: new Date(),
                                title: `Export complete.`,
                                type: EValidationSeverity.info,
                            }]));
                        }

                    } catch (error) {

                        application.QueueError({ error: error });

                    }
                    finally {
                        setViewModel(vm => ({
                            ...vm,
                            processing: false,
                        }));

                        dispatch(setPendingExport(false));
                    }

                }}
            />

            {!props.isReport && application.ChangeTypePermissions.groupStructure.import &&
                <DefaultButton
                    styles={panelButtonsStyles}
                    text='Switch to Import'
                    disabled={
                        viewModel.processing ||
                        !viewModel.importReady ||
                        state.status.gsImportPendingExport
                    }
                    onClick={() => {
                        setViewModel(t => ({ ...t, view: EViewState.import }));
                    }}
                />
            }

            <DefaultButton
                text='Cancel'
                onClick={() => {
                    if (props.isReport) {
                        props.onCancel?.();
                    } else {
                        props.setView(EView.entry);
                    }
                }}
                styles={panelButtonsStyles}
            />
        </div>;
    }

    const onRenderImportFooter = () => {
        return <div className={fadeIn}>
            <PrimaryButton text='Process'
                styles={panelButtonsStyles}
                disabled={fileUpload === null || !viewModel.importReady || !(state.status.viewType === EViewType.Modify) || viewModel.processing || !application.ChangeTypePermissions.groupStructure.import}
                onClick={async () => {

                    const _logs: IValidationMessage[] = []

                    try {

                        setViewModel(vm => ({
                            ...vm,
                            processing: true,
                        }));

                        // queue so the UI can update
                        await Delay(0);

                        const reqJsonRead = {
                            "$type": "BCBSSC.Azure.Portal.Models.RequestBatch, BCBSSC.Azure.Portal",
                            "FileBase64": fileUpload,
                            "ReadOnly": true,
                            "Requests": [
                                {
                                    "$type": "BCBSSC.Azure.Portal.Models.ReadSheetRequest, BCBSSC.Azure.Portal",
                                    "SheetName": "Import Template"
                                },
                                {
                                    "$type": "BCBSSC.Azure.Portal.Models.ReadSheetRequest, BCBSSC.Azure.Portal",
                                    "SheetName": "Departments"
                                },
                                {
                                    "$type": "BCBSSC.Azure.Portal.Models.ReadSheetRequest, BCBSSC.Azure.Portal",
                                    "SheetName": "CES Client Numbers"
                                },
                                {
                                    "$type": "BCBSSC.Azure.Portal.Models.ReadSheetRequest, BCBSSC.Azure.Portal",
                                    "SheetName": "Contacts"
                                },
                                {
                                    "$type": "BCBSSC.Azure.Portal.Models.ReadSheetRequest, BCBSSC.Azure.Portal",
                                    "SheetName": "Metadata"
                                },
                            ]
                        }

                        const buildResp = await dataSource.net.raw('POST', 'api/Excel/Read', reqJsonRead);
                        const buildRespBlob: IRequestBatchApi = await buildResp.json();

                        const rGroupNumbers: FAM_IGroupNumber[] = [];
                        const rDepartments: FAM_IDepartment[] = [];
                        const rCESNumbers: FAM_ICESNumber[] = [];

                        // Contacts
                        // ----------------------
                        const xlContacts = buildRespBlob.requests.find(q => q.sheetName === 'Contacts');
                        const xlContactsLookup: Record<string, FAM_IEntityReferenceValue> = {};
                        xlContacts?.rows.forEach((eachRow, eachRowIndex) => {

                            // skip first row, its the header
                            if (eachRowIndex === 0)
                                return;

                            const contact = eachRow['Contacts'];
                            const data = eachRow['Data'];

                            if (!data)
                                return;

                            const ref = JSON.parse(decodeURIComponent(atob(data))) as FAM_IEntityReferenceValue;

                            xlContactsLookup[contact] = ref;
                        });

                        // CES Client Number
                        // ----------------------
                        {
                            const xlCESClientNumbers = buildRespBlob.requests.find(q => q.sheetName === 'CES Client Numbers');

                            _logs.push({
                                date: new Date(),
                                title: `${(xlCESClientNumbers?.rows.length ?? 1) - 1} CES Client Number lines detected.`,
                                type: EValidationSeverity.info,
                            });


                            xlCESClientNumbers?.rows.forEach((eachRow, eachRowIndex) => {
                                // skip first row, its the header
                                if (eachRowIndex === 0)
                                    return;

                                const rowCesClientNumber = eachRow['CES Client Number'];
                                const rowID = eachRow['ID'];
                                const rowStatus = eachRow['Status'];

                                const cesClientNumber: FAM_ICESNumber = {
                                    cesNumber: rowCesClientNumber,
                                    deactivated: rowStatus === "Active" ? false : true,
                                    id: rowID,
                                };

                                rCESNumbers.push(cesClientNumber)

                                _logs.push({
                                    date: new Date(),
                                    title: `Row (${eachRowIndex}), CES Client Number (${cesClientNumber.cesNumber}) processed.`,
                                    type: EValidationSeverity.info,
                                });

                            })
                        }

                        // Group Number
                        // ----------------------
                        {
                            const xlGroupNumbers = buildRespBlob.requests.find(q => q.sheetName === 'Import Template');

                            _logs.push({
                                date: new Date(),
                                title: `${(xlGroupNumbers?.rows.length ?? 1) - 1} Group Number lines detected.`,
                                type: EValidationSeverity.info,
                            });

                            xlGroupNumbers?.rows.forEach((eachRow, eachRowIndex) => {
                                // skip first row, its the header
                                if (eachRowIndex === 0)
                                    return;

                                const rowCESClientNumber = eachRow['CES Client Number']?.trim();
                                const rowID = eachRow['ID'];
                                const rowGroupNumber = eachRow['Group Number'];
                                const rowMasterPrefix = eachRow['Master Prefix']?.trim();
                                const rowMasterBase = eachRow['Master Base']?.trim();
                                const rowSuffix = eachRow['Suffix']?.trim();

                                const rowPlan = eachRow['Plan'] === null ? null : eachRow['Plan']?.trim();
                                const rowAccountsReceivable = eachRow['Accounts Receivable'] === null ? null : eachRow['Accounts Receivable']?.trim();
                                const rowStopLoss = eachRow['Stop Loss'] === null ? null : eachRow['Stop Loss']?.trim();
                                const rowStatus = eachRow['Status'] === null ? null : eachRow['Status']?.trim();
                                const rowContact = eachRow['Contact'] === null ? null : eachRow['Contact']?.trim();
                                const rowGroupLeader = eachRow['Group Leader'] === null ? null : eachRow['Group Leader'];

                                // ok lets first search by the row id
                                let foundGN = state.data.json.groupStructure.groupNumbers.find(q => q.id === rowID);

                                let groupNumber = null as FAM_IGroupNumber | null;
                                const existingAccountsReceivableRef = foundGN?.accountsReceivableRef ? structuredClone(foundGN.accountsReceivableRef) : null;
                                const existingStopLossRef = foundGN?.stopLossRef ? structuredClone(foundGN.stopLossRef) : null;
                                // pick group number
                                if (foundGN) {
                                    groupNumber = structuredClone(foundGN);
                                } else {
                                    // group number for use if we are adding group number
                                    // -------------------------------------------------
                                    groupNumber = createGroupNumber(state.data);
                                };

                                if (rowID)
                                    groupNumber.id = rowID;

                                rGroupNumbers.push(groupNumber);

                                const cesNumberRef: FAM_IRef = { id: '', name: rowCESClientNumber };

                                // Properties
                                groupNumber.cesNumberRef = cesNumberRef
                                groupNumber.prefix = rowMasterPrefix;
                                groupNumber.suffix = rowSuffix;
                                groupNumber.base = rowMasterBase;

                                // Always calculate Group Number from components when all three exist
                                // This ensures consistency and makes components the source of truth
                                if (rowMasterPrefix && rowMasterBase && rowSuffix) {
                                    groupNumber.groupNumber = `${rowMasterPrefix}-${rowMasterBase}-${rowSuffix}`;
                                } else {
                                    // Fallback to Excel value if components are incomplete
                                    groupNumber.groupNumber = rowGroupNumber;
                                }

                                // Plan Mapping
                                // find matching element from model and create a reference
                                // --------------------------------------------------
                                if (rowPlan !== undefined)
                                    if (rowPlan === null || rowPlan.toLowerCase() === clearCommandText) {
                                        groupNumber.planRef = null
                                    } else {
                                        const t = state.data.json.plans.find(q => !q.deactivated && q.name.trim() === rowPlan)!;
                                        if (t) {
                                            groupNumber.planRef = {
                                                id: t.id,
                                                name: t.name
                                            }
                                        }
                                        else {
                                            _logs.push({
                                                date: new Date(),
                                                title: `Row (${eachRowIndex}), Plan (${rowPlan}) was not found.`,
                                                type: EValidationSeverity.warn
                                            });
                                        }
                                    }

                                // Accounts Receivable Mapping
                                // find matching element from model and create a reference
                                // --------------------------------------------------
                                if (rowAccountsReceivable !== undefined)
                                    if (rowAccountsReceivable === null || rowAccountsReceivable.toLowerCase() === clearCommandText) {
                                        groupNumber.accountsReceivableRef = null;
                                    } else {
                                        const t = state.data.json.claimsARs.find(q => !q.deactivated && q.name.trim() === rowAccountsReceivable)!;
                                        if (t) {
                                            groupNumber.accountsReceivableRef = {
                                                id: t.id,
                                                name: t.name
                                            }
                                        }
                                        else {

                                            _logs.push({
                                                date: new Date(),
                                                title: `Row (${eachRowIndex}), Accounts Receivable (${rowAccountsReceivable}) was not found.`,
                                                type: EValidationSeverity.warn
                                            });
                                        }
                                    }

                                // Change Type Validation: Accounts Receivable
                                // Fully Insured groups do not have AR - skip check
                                // --------------------------------------------------
                                {
                                    const isFullyInsured = state.data.json.fundingType?.value === 808900002;
                                    const arNotAllowed = !application.ChangeTypePermissions.groupStructure.groupNumber.link_accountsreceivable;
                                    const arChanged = (groupNumber.accountsReceivableRef?.id ?? null) !== (existingAccountsReceivableRef?.id ?? null);
                                    if (!isFullyInsured && arNotAllowed && arChanged) {
                                        _logs.push({
                                            date: new Date(),
                                            title: `Row (${eachRowIndex}), Group Number (${groupNumber.groupNumber ?? rowID}): Accounts Receivable was ignored on import.`,
                                            description: `Accounts Receivable (${groupNumber.accountsReceivableRef ? `"${groupNumber.accountsReceivableRef.name}"` : 'clear'}) was not imported because Change Type "${application.ChangeTypePermissions.name}" does not allow it. Use a Financial or Comprehensive Change Type to associate Accounts Receivable.`,
                                            type: EValidationSeverity.warn
                                        });
                                        groupNumber.accountsReceivableRef = existingAccountsReceivableRef;
                                    }
                                }

                                // Stop Loss Mapping
                                // find matching element from model and create a reference
                                // --------------------------------------------------
                                if (rowStopLoss !== undefined)
                                    if (rowStopLoss === null || rowStopLoss.toLowerCase() === clearCommandText) {
                                        groupNumber.stopLossRef = null;
                                    } else {

                                        const t = state.data.json.stopLoss.find(q => !q.deactivated && q.name.trim() === rowStopLoss)!;

                                        if (t) {
                                            groupNumber.stopLossRef = {
                                                id: t.id,
                                                name: t.name
                                            }
                                        }
                                        else {

                                            _logs.push({
                                                date: new Date(),
                                                title: `Row (${eachRowIndex}), Stop Loss (${rowStopLoss}) was not found.`,
                                                type: EValidationSeverity.warn
                                            });
                                        }

                                    }

                                // Change Type Validation: Stop Loss
                                // Fully Insured groups do not have Stop Loss - skip check
                                // --------------------------------------------------
                                {
                                    const isFullyInsured = state.data.json.fundingType?.value === 808900002;
                                    const slNotAllowed = !application.ChangeTypePermissions.groupStructure.groupNumber.link_stoploss;
                                    const slChanged = (groupNumber.stopLossRef?.id ?? null) !== (existingStopLossRef?.id ?? null);
                                    if (!isFullyInsured && slNotAllowed && slChanged) {
                                        _logs.push({
                                            date: new Date(),
                                            title: `Row (${eachRowIndex}), Group Number (${groupNumber.groupNumber ?? rowID}): Stop Loss was ignored on import.`,
                                            description: `Stop Loss (${groupNumber.stopLossRef ? `"${groupNumber.stopLossRef.name}"` : 'clear'}) was not imported because Change Type "${application.ChangeTypePermissions.name}" does not allow it. Use a Financial or Comprehensive Change Type to associate Stop Loss.`,
                                            type: EValidationSeverity.warn
                                        });
                                        groupNumber.stopLossRef = existingStopLossRef;
                                    }
                                }

                                // Status
                                // --------------------------------------------------
                                if (rowStatus) {
                                    if (rowStatus === "Active") {

                                        groupNumber.deactivated = false;

                                        groupNumber.status = {
                                            name: FAM_EBenefitStatus[FAM_EBenefitStatus["In-Progress"]],
                                            type: "optionset",
                                            value: FAM_EBenefitStatus["In-Progress"]
                                        };

                                    } else if (rowStatus === "Cancelled") {

                                        groupNumber.deactivated = false

                                        groupNumber.status = {
                                            name: FAM_EBenefitStatus[FAM_EBenefitStatus["Cancelled"]],
                                            type: "optionset",
                                            value: FAM_EBenefitStatus["Cancelled"]
                                        };

                                    } else if (rowStatus === "Deactivated") {
                                        groupNumber.deactivated = true
                                    } else {

                                        _logs.push({
                                            date: new Date(),
                                            title: `Row (${eachRowIndex}) skipped, Status did not have a matching value.`,
                                            type: EValidationSeverity.error
                                        });

                                        return;
                                    }
                                }

                                groupNumber.fields.forEach(eachField => {

                                    const attribute = state.data.config.cache.map_bcbs_config_attribute[eachField.configRef.id];
                                    const masterAttribute = state.data.config.cache.map_bcbs_config_masterattribute[attribute._bcbs_config_masterattributeid_value!];

                                    // Change Type Validation: Flexible Fields
                                    // If this field doesn't belong to the current change type and the row
                                    // has a value for it, warn and skip — preserve the existing stored value.
                                    // --------------------------------------------------
                                    {
                                        const ctValue = state.data.json.changeType?.value ?? 0;
                                        if (EvaluateFieldDisability(ctValue, attribute)) {
                                            const rowHasValue = eachRow[attribute.bcbs_name!] !== undefined
                                                && eachRow[attribute.bcbs_name!] !== null
                                                && eachRow[attribute.bcbs_name!] !== '';
                                            if (rowHasValue) {
                                                _logs.push({
                                                    date: new Date(),
                                                    title: `Row (${eachRowIndex}), field "${attribute.bcbs_name}" is not applicable for Change Type "${application.ChangeTypePermissions.name}" and has been skipped.`,
                                                    description: `The field "${attribute.bcbs_name}" does not belong to the current Change Type. The existing value has been preserved. Remove this column from the import file or use a compatible Change Type.`,
                                                    type: EValidationSeverity.warn
                                                });
                                            }
                                            return;
                                        }
                                    }

                                    if (eachRow[attribute.bcbs_name!] !== undefined) {

                                        const valueText = eachRow[attribute.bcbs_name!];
                                        const control = masterAttribute.bcbs_attributetypecode as EControlType;

                                        try {

                                            //special ones
                                            let lookupTemplate = null as any;

                                            if (masterAttribute.bcbs_lookupentitylogicalname === "contact") {

                                                let thisContact: string | null = null;

                                                // Contact
                                                // bcbs_contactid
                                                // https://bcbsonline.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=dc7c1bc6-d18d-ec11-b400-0022480a93ed
                                                if (attribute.bcbs_config_attributeid === $att_$group_number_$bcbs_contactid_id)
                                                    thisContact = rowContact;

                                                // Group Leader
                                                // bcbs_groupleaderid
                                                // https://bcbsonline.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=c07c1bc6-d18d-ec11-b400-0022480a93ed
                                                if (attribute.bcbs_config_attributeid === $att_$group_number_$bcbs_groupleaderid_id)
                                                    thisContact = rowGroupLeader;

                                                // Contacts
                                                // find matching element from model and create a reference
                                                // --------------------------------------------------
                                                if (thisContact !== "")
                                                    if (thisContact && contactsByAccountId) {

                                                        // let wsRegex = /^\s+|\s+$/g;
                                                        // const rowContactClean = thisContact.replaceAll(wsRegex, "");
                                                        // || (q.firstname ?? '' + q.middlename ?? '' + q.lastname ?? '') === thisContactClean

                                                        // const contactId = thisContact.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/g);

                                                        // lets first try extracting using just a contact id
                                                        let foundContact = xlContactsLookup[thisContact];

                                                        // HERE
                                                        if (foundContact) {
                                                            lookupTemplate = foundContact;
                                                            debugger
                                                        } else {
                                                            _logs.push({
                                                                date: new Date(),
                                                                title: `Row (${eachRowIndex}), Contact (${rowContact}) was not found.`,
                                                                type: EValidationSeverity.warn
                                                            });
                                                        }
                                                    }
                                            }

                                            const value = friendlyValueToFlexibleFieldValue(
                                                state.data.config,
                                                valueText,
                                                attribute,
                                                masterAttribute,
                                                lookupTemplate
                                            );

                                            if (valueText === null || valueText === clearCommandText) {
                                                _logs.push({
                                                    date: new Date(),
                                                    title: `Row (${eachRowIndex}), field (${attribute.bcbs_name}/${attribute.bcbs_config_attributeid}) was requested to be cleared.`,
                                                    type: EValidationSeverity.info
                                                });
                                            } else if (valueText === undefined) {
                                                throw new Error('failed to load value');
                                            }

                                            eachField.values[0].value = value;

                                        } catch (error) {

                                            _logs.push({
                                                date: new Date(),
                                                title: `Row (${eachRowIndex}), field (${attribute.bcbs_name}/${attribute.bcbs_config_attributeid}) mapping value failed.`,
                                                type: EValidationSeverity.warn
                                            });
                                        }

                                    }
                                });

                                // Working Cancellations
                                // ------------------------------
                                // Cancellation Effective Date
                                // Pay Run Out Claims
                                // Pay Run Out Claims How Many Months
                                // Run Out Claims End Date

                                // is the row getting cancelled?
                                if (rowStatus === "Cancelled" &&
                                    eachRow['Cancellation Effective Date'] !== undefined && eachRow['Cancellation Effective Date'] !== null && eachRow['Cancellation Effective Date'] !== '' &&
                                    eachRow['Pay Run Out Claims'] !== undefined && eachRow['Pay Run Out Claims'] !== null && eachRow['Pay Run Out Claims'] !== '' &&
                                    eachRow['Pay Run Out Claims How Many Months'] !== undefined && eachRow['Pay Run Out Claims How Many Months'] !== null && eachRow['Pay Run Out Claims How Many Months'] !== '' &&
                                    (eachRow['Run Out Claims End Date'] === undefined || eachRow['Run Out Claims End Date'] === null || eachRow['Run Out Claims End Date'] === '')
                                ) {

                                    const cancellationFields = CancellationFieldsByCategory[$cat_$group_number_id];

                                    const fieldsMap = ArrayToMapByPredicateWithMap(groupNumber.fields, eField => eField.configRef.id, (eField, index) => {
                                        const attribute = state.data.config.cache.map_bcbs_config_attribute[eField.configRef.id];
                                        const masterAttribute = state.data.config.cache.map_bcbs_config_masterattribute[attribute._bcbs_config_masterattributeid_value!];
                                        const ret: IFieldReference = {
                                            index: index,
                                            field: eField,
                                            attribute: attribute,
                                            masterAttribute: masterAttribute,
                                        }
                                        return ret;
                                    });

                                    const cancellationId = CancellationFieldsByCategory[$cat_$group_number_id]["Cancellation Effective Date"];;
                                    const cEffectiveDateField = fieldsMap.get(cancellationId)?.field

                                    if (cEffectiveDateField && cEffectiveDateField?.values?.[0]?.value)
                                        CalculateRunOutClaimsEndDate(
                                            state.data.json,
                                            cancellationFields,
                                            cEffectiveDateField,
                                            fieldsMap,
                                            (calculatedValue, fieledAttribute, fieldValue) => {
                                                // custom action
                                                fieldValue.value = calculatedValue;
                                            });
                                }

                                _logs.push({
                                    date: new Date(),
                                    title: `Row (${eachRowIndex}), Group Number (${groupNumber.groupNumber}) processed.`,
                                    type: EValidationSeverity.info,
                                });

                            });

                            // Validation: Check that CES Number references exist
                            // ----------------------
                            {
                                const cesNumberLookup = new Set(rCESNumbers.map(ces => ces.cesNumber));

                                xlGroupNumbers?.rows.forEach((eachRow, eachRowIndex) => {
                                    if (eachRowIndex === 0) return; // Skip header

                                    const rowCESClientNumber = eachRow['CES Client Number']?.trim();
                                    const rowGroupNumber = eachRow['Group Number'];

                                    // Check if a CES Number is referenced
                                    if (rowCESClientNumber) {
                                        // Verify it exists in the imported CES Numbers
                                        if (!cesNumberLookup.has(rowCESClientNumber)) {
                                            _logs.push({
                                                date: new Date(),
                                                title: `Row ${eachRowIndex}: Invalid CES Number reference`,
                                                description: `Group Number "${rowGroupNumber}" references CES Client Number "${rowCESClientNumber}", but this CES Number does not exist in the "CES Client Numbers" sheet. Please ensure all referenced CES Numbers are present in the import file.`,
                                                type: EValidationSeverity.error
                                            });
                                        }
                                    }
                                });
                            }

                            // Validation: Check for duplicate IDs in imported Group Numbers
                            // ----------------------
                            {
                                const idCounts = new Map<string, number>();
                                const idRows = new Map<string, number[]>();

                                xlGroupNumbers?.rows.forEach((eachRow, eachRowIndex) => {
                                    if (eachRowIndex === 0) return; // Skip header

                                    const rowID = eachRow['ID'];
                                    if (rowID) {
                                        const count = idCounts.get(rowID) || 0;
                                        idCounts.set(rowID, count + 1);

                                        const rows = idRows.get(rowID) || [];
                                        rows.push(eachRowIndex);
                                        idRows.set(rowID, rows);
                                    }
                                });

                                // Report duplicates
                                idCounts.forEach((count, id) => {
                                    if (count > 1) {
                                        const rows = idRows.get(id)?.join(', ') || '';
                                        _logs.push({
                                            date: new Date(),
                                            title: `Duplicate ID detected: "${id}" appears ${count} times (rows: ${rows})`,
                                            description: 'Each Group Number must have a unique ID. Duplicate IDs may cause import conflicts or data overwrite issues.',
                                            type: EValidationSeverity.error
                                        });
                                    }
                                });
                            }

                            // Validation: Check for duplicate Group Numbers in imported data
                            // ----------------------
                            {
                                const groupNumberCounts = new Map<string, number>();
                                const groupNumberRows = new Map<string, number[]>();

                                xlGroupNumbers?.rows.forEach((eachRow, eachRowIndex) => {
                                    if (eachRowIndex === 0) return; // Skip header

                                    const rowGroupNumber = eachRow['Group Number'];
                                    if (rowGroupNumber) {
                                        const count = groupNumberCounts.get(rowGroupNumber) || 0;
                                        groupNumberCounts.set(rowGroupNumber, count + 1);

                                        const rows = groupNumberRows.get(rowGroupNumber) || [];
                                        rows.push(eachRowIndex);
                                        groupNumberRows.set(rowGroupNumber, rows);
                                    }
                                });

                                // Report duplicates
                                groupNumberCounts.forEach((count, groupNumber) => {
                                    if (count > 1) {
                                        const rows = groupNumberRows.get(groupNumber)?.join(', ') || '';
                                        _logs.push({
                                            date: new Date(),
                                            title: `Duplicate Group Number detected: "${groupNumber}" appears ${count} times (rows: ${rows})`,
                                            description: 'Each Group Number must be unique within the import file. Duplicate Group Numbers will cause conflicts.',
                                            type: EValidationSeverity.error
                                        });
                                    }
                                });
                            }

                            // Validation: Check that Group Number matches Master Prefix-Master Base-Suffix pattern
                            // ----------------------
                            {
                                xlGroupNumbers?.rows.forEach((eachRow, eachRowIndex) => {
                                    if (eachRowIndex === 0) return; // Skip header

                                    const rowGroupNumber = eachRow['Group Number'];
                                    const rowMasterPrefix = eachRow['Master Prefix']?.trim();
                                    const rowMasterBase = eachRow['Master Base']?.trim();
                                    const rowSuffix = eachRow['Suffix']?.trim();

                                    // Only validate if all components and group number exist
                                    if (rowGroupNumber && rowMasterPrefix && rowMasterBase && rowSuffix) {
                                        const expectedGroupNumber = `${rowMasterPrefix}-${rowMasterBase}-${rowSuffix}`;

                                        if (rowGroupNumber !== expectedGroupNumber) {
                                            _logs.push({
                                                date: new Date(),
                                                title: `Row ${eachRowIndex}: Group Number mismatch detected`,
                                                description: `Group Number is "${rowGroupNumber}" but should be "${expectedGroupNumber}" based on Master Prefix-Master Base-Suffix.\nPlease ensure the Group Number formula is calculating correctly or manually update it to match the components.`,
                                                type: EValidationSeverity.error
                                            });
                                        }
                                    }
                                });
                            }

                            // Validation: Check for duplicate Group Numbers in Excel file
                            // ----------------------
                            const groupNumberCounts = new Map<string, number>();
                            const groupNumberRows = new Map<string, number[]>();

                            xlGroupNumbers?.rows.forEach((eachRow, eachRowIndex) => {
                                if (eachRowIndex === 0) return; // Skip header

                                const rowGroupNumber = eachRow['Group Number'];
                                if (rowGroupNumber) {
                                    const count = groupNumberCounts.get(rowGroupNumber) || 0;
                                    groupNumberCounts.set(rowGroupNumber, count + 1);

                                    const rows = groupNumberRows.get(rowGroupNumber) || [];
                                    rows.push(eachRowIndex);
                                    groupNumberRows.set(rowGroupNumber, rows);
                                }
                            });

                            // Report duplicates with exact row numbers from Excel
                            groupNumberCounts.forEach((count, groupNumber) => {
                                if (count > 1) {
                                    const rows = groupNumberRows.get(groupNumber)?.join(', ') || '';
                                    _logs.push({
                                        date: new Date(),
                                        title: `Duplicate Group Number detected: "${groupNumber}" appears ${count} times (rows: ${rows})`,
                                        description: 'Each Group Number must be unique within the import file. Please fix the duplicates in your Excel file before importing.',
                                        type: EValidationSeverity.error
                                    });
                                }
                            });

                            // Validation: Check Cancellation Effective Date matches Group Configuration Effective Date
                            // Validation Edit - Display a hard edit when the Subgroup or Benefit Cancellation Effective Date does not match the Effective Date of the Configuration
                            // ----------------------
                            {
                                const jsonEffectiveDate = state.data.json.effectiveDate?.value;
                                const reconciliationFlag = state.data.json.reconciliation?.value ?? false;

                                xlGroupNumbers?.rows.forEach((eachRow, eachRowIndex) => {
                                    if (eachRowIndex === 0) return; // Skip header

                                    const rowStatus = eachRow['Status']?.trim();
                                    const rowGroupNumber = eachRow['Group Number'];
                                    const rowCancellationEffectiveDate = eachRow['Cancellation Effective Date'];

                                    // Validate cancelled Group Numbers
                                    if (rowStatus === "Cancelled") {

                                        // Check if cancellation date is missing
                                        if (!rowCancellationEffectiveDate || rowCancellationEffectiveDate.trim() === '') {
                                            _logs.push({
                                                date: new Date(),
                                                title: `The Group Number (${rowGroupNumber}) Cancellation Effective Date is required for cancelled Group Numbers.`,
                                                description: `Status: Cancelled\nCancellation Effective Date: (blank)\nRow: ${eachRowIndex}\n\nPlease provide a Cancellation Effective Date for this cancelled Group Number.`,
                                                type: EValidationSeverity.error
                                            });
                                        }
                                        // 598407 - The reconciliation flag allows dates to differ
                                        else if (!reconciliationFlag) {
                                            // Parse the cancellation effective date from Excel
                                            // Use the same approach as field processing - get the attribute config
                                            const cancellationAttributeId = $matt_$group_number_$bcbs_cancellationeffectivedate_id; // Cancellation Effective Date master attribute
                                            const cancellationConfigId = $att_$group_number_$bcbs_cancellationeffectivedate_id; // Cancellation Effective Date config attribute

                                            const attribute = state.data.config.cache.map_bcbs_config_attribute[cancellationConfigId];
                                            const masterAttribute = state.data.config.cache.map_bcbs_config_masterattribute[cancellationAttributeId];

                                            let gnCancellationEffectiveDate: string | null = null;
                                            let parseError = false;

                                            try {
                                                // Convert Excel value to proper format using the same utility as field processing
                                                const value = friendlyValueToFlexibleFieldValue(
                                                    state.data.config,
                                                    rowCancellationEffectiveDate,
                                                    attribute,
                                                    masterAttribute,
                                                    null
                                                );

                                                if (value && value.type === 'date' && value.value) {
                                                    gnCancellationEffectiveDate = value.value;
                                                } else {
                                                    parseError = true;
                                                }
                                            } catch {
                                                parseError = true;
                                            }

                                            // If parsing failed, show an error
                                            if (parseError) {
                                                _logs.push({
                                                    date: new Date(),
                                                    title: `Row ${eachRowIndex}: Invalid Cancellation Effective Date format.`,
                                                    description: `Group Number: ${rowGroupNumber}\nCancellation Effective Date: "${rowCancellationEffectiveDate}"\n\nThe date could not be parsed. Please ensure it is in a valid date format (MM/DD/YYYY or YYYY-MM-DD).`,
                                                    type: EValidationSeverity.error
                                                });
                                            }
                                            // Compare dates if parsing succeeded
                                            else if (jsonEffectiveDate !== gnCancellationEffectiveDate) {
                                                const gnEffectiveDateValue = gnCancellationEffectiveDate
                                                    ? formatDate(parseDate(gnCancellationEffectiveDate, 'yyyy-MM-ddThh:mm:ssZ as local'), 'local:MM/dd/yyyy')
                                                    : "(blank)";

                                                const snapshotEffectiveDateValue = jsonEffectiveDate
                                                    ? formatDate(parseDate(jsonEffectiveDate, 'yyyy-MM-ddThh:mm:ssZ as local'), 'local:MM/dd/yyyy')
                                                    : "(blank)";

                                                _logs.push({
                                                    date: new Date(),
                                                    title: `The Group Number (${rowGroupNumber}) Cancellation Effective Date must match the Group Configuration Effective Date.`,
                                                    description: `Group Configuration Effective Date: ${snapshotEffectiveDateValue}\nCancellation Effective Date: ${gnEffectiveDateValue}\nRow: ${eachRowIndex}`,
                                                    type: EValidationSeverity.error
                                                });
                                            }
                                        }
                                    }
                                });
                            }
                        }

                        // Departments
                        // ----------------------
                        {
                            const xlDepartments = buildRespBlob.requests.find(q => q.sheetName === 'Departments');

                            _logs.push({
                                date: new Date(),
                                title: `${(xlDepartments?.rows.length ?? 1) - 1} Department lines detected.`,
                                type: EValidationSeverity.info,
                            });

                            xlDepartments?.rows.forEach((eachRow, eachRowIndex) => {
                                // skip first row, its the header
                                if (eachRowIndex === 0)
                                    return;

                                const rowCesClientNumber = eachRow['CES Client Number'];
                                const rowID = eachRow['ID'];
                                const rowDescription = eachRow['Description'];
                                const rowDepartmentNumber = eachRow['Department Number'];
                                const rowStatus = eachRow['Status'];

                                let department = null as FAM_IDepartment | null;

                                department = {
                                    id: rowID,
                                    description: rowDescription,
                                    name: rowDepartmentNumber,
                                    deactivated: rowStatus === "Active" ? false : true,
                                    cesNumberRef: { id: '', name: rowCesClientNumber },
                                    fields: [],
                                };

                                _logs.push({
                                    date: new Date(),
                                    title: `Row (${eachRowIndex}), Department (${department.name}) processed.`,
                                    type: EValidationSeverity.info,
                                });

                                rDepartments.push(department);

                            });

                            // Validation: Check that CES Number references exist
                            // ----------------------
                            {
                                const cesNumberLookup = new Set(rCESNumbers.map(ces => ces.cesNumber));

                                xlDepartments?.rows.forEach((eachRow, eachRowIndex) => {
                                    if (eachRowIndex === 0) return; // Skip header

                                    const rowCesClientNumber = eachRow['CES Client Number'];
                                    const rowDepartmentNumber = eachRow['Department Number'];

                                    // Check if a CES Number is referenced
                                    if (rowCesClientNumber) {
                                        // Verify it exists in the imported CES Numbers
                                        if (!cesNumberLookup.has(rowCesClientNumber)) {
                                            _logs.push({
                                                date: new Date(),
                                                title: `Row ${eachRowIndex}: Invalid CES Number reference`,
                                                description: `Department "${rowDepartmentNumber}" references CES Client Number "${rowCesClientNumber}", but this CES Number does not exist in the "CES Client Numbers" sheet. Please ensure all referenced CES Numbers are present in the import file.`,
                                                type: EValidationSeverity.error
                                            });
                                        }
                                    }
                                });
                            }
                        }

                        // Metadata
                        const rMetadata: Record<string, string> = {}
                        {
                            const xlMetadata = buildRespBlob.requests.find(q => q.sheetName === 'Metadata');

                            xlMetadata?.rows.forEach(eachRow => {
                                const key = eachRow["Key"];
                                const value = eachRow["Value"];
                                const description = eachRow["Description"];

                                rMetadata[key] = value;
                                return [key, value, description]
                            })
                        }


                        // Transform and Reconciliations
                        // -----------------------------------------------------------------------
                        rCESNumbers.forEach(eachCESNumber => {

                            // no id? using alternate key to match, trying to not import duplicates
                            if (!eachCESNumber.id) {
                                const found = state.data.json.groupStructure.cesNumbers.find(t => t.cesNumber === eachCESNumber.cesNumber);

                                if (found) {
                                    eachCESNumber.id = found.id;
                                }
                            }

                            // This is new
                            if (!eachCESNumber.id) {
                                eachCESNumber.id = uuidv4();
                            }
                        })

                        rGroupNumbers.forEach(eachGroupNumber => {

                            // no id? using alternate key to match, trying to not import duplicates
                            if (!eachGroupNumber.id) {
                                const found = state.data.json.groupStructure.groupNumbers.find(t => t.groupNumber === eachGroupNumber.groupNumber);

                                if (found) {
                                    eachGroupNumber.id = found.id;
                                }
                            }

                            // This is new
                            if (!eachGroupNumber.id) {
                                eachGroupNumber.id = uuidv4();
                            }

                            if (eachGroupNumber.cesNumberRef?.name) {
                                const foundCESNumber = rCESNumbers.find(t => t.cesNumber === eachGroupNumber.cesNumberRef?.name);
                                if (foundCESNumber)
                                    eachGroupNumber.cesNumberRef = {
                                        id: foundCESNumber.id!,
                                        name: foundCESNumber.cesNumber!,
                                    };
                                else
                                    eachGroupNumber.cesNumberRef = null;
                            }
                        });

                        rDepartments.forEach(eachDepartment => {

                            // no id? using alternate key to match, trying to not import duplicates
                            if (!eachDepartment.id) {
                                const found = state.data.json.groupStructure.departments.find(t => t.name === eachDepartment.name);

                                if (found) {
                                    eachDepartment.id = found.id;
                                }
                            }

                            // This is new
                            if (!eachDepartment.id) {
                                eachDepartment.id = uuidv4();
                            }

                            if (eachDepartment.cesNumberRef?.name) {
                                const foundCESNumber = rCESNumbers.find(t => t.cesNumber === eachDepartment.cesNumberRef?.name);
                                if (foundCESNumber)
                                    eachDepartment.cesNumberRef = {
                                        id: foundCESNumber.id!,
                                        name: foundCESNumber.cesNumber!,
                                    };
                                else
                                    eachDepartment.cesNumberRef = null;

                            }
                        });



                        // Building Global Scope
                        // ---------------------------------------------
                        const globalScope: IVisibilityScope = BuildVisibilityScope(
                            null,
                            state.data.json,
                            null,
                            null
                        );

                        applyVisibilityGroupNumbers(
                            state.data.json,
                            state.data.config,
                            globalScope,
                            rGroupNumbers
                        );

                        // Post processing, map ces client number
                        updateGroupConfig({
                            groupNumbers: rGroupNumbers,
                            departments: rDepartments,
                            cesNumbers: rCESNumbers,
                            metaData: rMetadata,
                            logs: _logs,
                        });


                    } catch (error) {
                        application.QueueError({ error: error });
                    } finally {
                        setViewModel(vm => ({ ...vm, processing: false }));
                    }
                }}
            />
            <DefaultButton
                text='Cancel'
                onClick={() => { props.setView(EView.entry); }}
                styles={panelButtonsStyles}
            />
        </div>;
    }

    const onRenderResults = () => {
        const clicker = <Link onClick={(event) => {

            const csvSanitize = (input: string) => {
                const escapedStr = `"${input.replace(/"/g, '""')}"`;
                return escapedStr;
            }

            const messages = prompts.map(q => {
                return [
                    q.date?.toISOString(),
                    q.type,
                    csvSanitize(q.title),
                    csvSanitize(q.description ?? "")
                ].join(',')
            });

            if (messages[0] !== 'date,type,title,description')
                messages.unshift('date,type,title,description');

            const fileName = `${state.data.json.bcbs_shortname ?? state.data.snapshot?.bcbs_groupconfigurationsnapshotid}-gs-${new Date().getTime()}.log.csv`;
            const blob = new Blob([messages.join(`\r\n`)], { type: 'text/plan;charset=utf-8' });
            saveAs(blob, fileName, { autoBom: true });
        }}>
            here
        </Link>;

        return <Stack tokens={{ childrenGap: 15 }} styles={{ root: { marginTop: '10px' } }}>
            <Checkbox
                label="Hide information messages"
                checked={viewModel.hideInfoMessages}
                onChange={(event, checked) => setViewModel(t => ({ ...t, hideInfoMessages: checked ?? false }))}
            />
            <div>
                <Text>Click&nbsp;{clicker}&nbsp;to download log details.</Text>
            </div>


            <Stack
                tokens={{ childrenGap: 8 }}
                styles={{ root: { marginTop: '15px', marginBottom: '15px' } }}
            >
                <Stack>
                    {prompts.map((vm, index) => {
                        if (viewModel.hideInfoMessages)
                            if (vm.type === EValidationSeverity.info)
                                return <></>;

                        return <ValidationItem
                            key={index}
                            data={vm}
                        />;
                    })}
                </Stack>
            </Stack>
        </Stack>
    }

    const onRenderResultsFooter = () => {
        return <div className={fadeIn}>
            <PrimaryButton text='Done'
                styles={panelButtonsStyles}
                onClick={async () => {
                    props.onDismiss?.();
                }}
            />
            <DefaultButton
                text='Cancel'
                disabled
                onClick={() => {
                    props.onDismiss?.();
                }}
            />
        </div>;
    }

    const updateGroupConfig = async (xSaveModel: ISaveTransaction) => {
        const _logs: IValidationMessage[] = [];

        let saveModel: ISaveTransaction | null = { ...xSaveModel };

        const saveModelLogs = saveModel?.logs;

        try {

            // create validation if it does not already exists
            if (!saveModel.validation) {
                saveModel.validation = {
                    validationMessages: [],
                    state: EValidationState.PendingUser,
                    data: xSaveModel.validation?.data,
                }

                const validations = saveModel.validation?.validationMessages!;
                {
                    const { groupNumbers, cesNumbers, departments, metaData } = saveModel;

                    // Add error-level logs from parsing to validation messages
                    // This includes duplicate checks and Group Number mismatch validations
                    if (saveModelLogs) {
                        saveModelLogs
                            .filter(log => log.type === EValidationSeverity.error)
                            .forEach(errorLog => validations.push(errorLog));
                    }

                    // const stateCESNumberById = ArrayToLookupByPredicate(state.data.json.groupStructure.cesNumbers, t => t.id);
                    // const stateGroupNumbersById = ArrayToLookupByPredicate(state.data.json.groupStructure.groupNumbers, t => t.id);
                    // const stateDepartmentsById = ArrayToLookupByPredicate(state.data.json.groupStructure.departments, t => t.id);

                    // CES Client Numbers
                    // -----------------------
                    {
                        const cesClientNumberByCESNumberTest: Record<string, FAM_ICESNumber[]> = {};
                        const cesClientNumberByIdTest: Record<string, FAM_ICESNumber[]> = {};

                        cesNumbers.forEach(eachCESNumber => {

                            if (!eachCESNumber.deactivated) {
                                if (!eachCESNumber.id) {
                                    validations.push({
                                        title: `CES Client Number (${eachCESNumber.cesNumber}) is missing ID.`,
                                        description: `This CES Client Number ID has been specified multiple times in Group Structure\nYou may need to change CES Number or deactivate one(s) that are no longer active.`,
                                        type: EValidationSeverity.error,
                                    });
                                }
                            }

                            if (!eachCESNumber.deactivated)
                                if (!eachCESNumber.cesNumber) {
                                    validations.push({
                                        title: `CES Client Number with ID (${eachCESNumber.id}) is missing CES Number.`,
                                        type: EValidationSeverity.error,
                                    });
                                }


                            if (!eachCESNumber.deactivated) {
                                if (eachCESNumber.cesNumber) {

                                    cesClientNumberByCESNumberTest[eachCESNumber.cesNumber] = cesClientNumberByCESNumberTest[eachCESNumber.cesNumber] ?? [];
                                    cesClientNumberByCESNumberTest[eachCESNumber.cesNumber].push(eachCESNumber);

                                    if (cesClientNumberByCESNumberTest[eachCESNumber.cesNumber].length > 1)
                                        validations.push({
                                            title: `CES Client Number (${eachCESNumber.cesNumber}) is not unique.`,
                                            description: `This CES Client Number has been specified multiple times in Group Structure\nYou may need to change CES Number or deactivate one(s) that are no longer active.`,
                                            type: EValidationSeverity.error,
                                        });
                                }
                            }


                            if (!eachCESNumber.deactivated) {
                                if (eachCESNumber.id) {

                                    cesClientNumberByIdTest[eachCESNumber.id] = cesClientNumberByIdTest[eachCESNumber.id] ?? [];
                                    cesClientNumberByIdTest[eachCESNumber.id].push(eachCESNumber);

                                    if (cesClientNumberByIdTest[eachCESNumber.id].length > 1)
                                        validations.push({
                                            title: `CES Client Number (${eachCESNumber.cesNumber}) has a duplicated ID (${eachCESNumber.id}).`,
                                            description: `This CES Client Number ID has been specified multiple times in Group Structure\nYou may need to change CES Number or deactivate one(s) that are no longer active.`,
                                            type: EValidationSeverity.error,
                                        });
                                }
                            }


                        });
                    }

                    // Group Numbers
                    // -----------------------
                    {
                        const groupNumberByGroupNumberTest: Record<string, FAM_IGroupNumber[]> = {};
                        const groupNumberByIdTest: Record<string, FAM_IGroupNumber[]> = {};

                        groupNumbers.forEach(eachGroupNumber => {
                            if (!eachGroupNumber.deactivated)
                                if (!eachGroupNumber.id)
                                    validations.push({
                                        title: `Group Number (${eachGroupNumber.groupNumber}) is missing ID.`,
                                        type: EValidationSeverity.error,
                                    });


                            // is Group Number set ?
                            if (!eachGroupNumber.deactivated)
                                if (!eachGroupNumber.groupNumber)
                                    validations.push({
                                        title: `Group Number with ID (${eachGroupNumber.id}) is missing Group Number.`,
                                        type: EValidationSeverity.error,
                                        description: `A Group Number is required and serves as the unique identifier for each group. This issue may occur if the import file is incomplete, contains invalid data, or if Excel formulas did not calculate properly before import (excel needs to save the file at least once). Please verify the file and ensure all Group Numbers are present and correctly generated.`,
                                    });

                            // Verify Group Number -> CES Client number exist prior to importing
                            if (!eachGroupNumber.deactivated) {
                                const cesNumberFound = cesNumbers.find(t => t.cesNumber === eachGroupNumber.cesNumberRef?.name);
                                if (!cesNumberFound) {
                                    validations.push({
                                        title: `Group Number (${eachGroupNumber.groupNumber}) is referencing an invalid CES Number (${eachGroupNumber.cesNumberRef?.name}).`,
                                        type: EValidationSeverity.error,
                                    });
                                }
                            }
                        });
                    }

                    // Departments
                    // -----------------------
                    {
                        const departmentByIdTest: Record<string, FAM_IDepartment[]> = {};
                        departments.forEach(eachDepartment => {

                            // is department valid?
                            if (!eachDepartment.deactivated)
                                if (!eachDepartment.id)
                                    validations.push({
                                        title: `Department (${eachDepartment.name}) is missing ID.`,
                                        type: EValidationSeverity.error,
                                    });

                            // is department valid?
                            if (!eachDepartment.deactivated)
                                if (!eachDepartment.name)
                                    validations.push({
                                        title: `Department with ID (${eachDepartment.id}) is missing Department Number.`,
                                        type: EValidationSeverity.error,
                                    });

                            // Making sure Department ids are unique
                            if (!eachDepartment.deactivated)
                                if (eachDepartment.id) {

                                    departmentByIdTest[eachDepartment.id] = departmentByIdTest[eachDepartment.id] ?? [];

                                    departmentByIdTest[eachDepartment.id].push(eachDepartment);

                                    if (departmentByIdTest[eachDepartment.id].length > 1)
                                        validations.push({
                                            title: `Department (${eachDepartment.name}) has a duplicated ID (${eachDepartment.id}).`,
                                            type: EValidationSeverity.error,
                                        });
                                }

                            //  Verify Department -> CES Client number exist prior to importing
                            if (!eachDepartment.deactivated) {
                                const cesNumberFound = cesNumbers.find(t => t.cesNumber === eachDepartment.cesNumberRef?.name);
                                if (!cesNumberFound) {
                                    validations.push({
                                        title: `Department (${eachDepartment.name}) is referencing an invalid CES Number (${eachDepartment.cesNumberRef?.name}).`,
                                        type: EValidationSeverity.error,
                                    });
                                }
                            }
                        });
                    }

                    // Metadata
                    // -----------------------
                    {
                        if (metaData.isReport === 'true') {
                            validations.push({
                                title: `This file is a Report and cannot be imported.`,
                                description: `Reports are full, read-only exports generated for review purposes. All columns are locked and their values are not used during import. Please use a standard Export file to import changes.`,
                                type: EValidationSeverity.error,
                            });
                        }

                        if (metaData.checksum) {

                            const checksum = cyrb53(JSON.stringify(state.data.json.groupStructure)).toLocaleString();
                            if (metaData.checksum !== checksum) {
                                validations.push({
                                    title: `Only import the group structure that was previously exported on this configuration.`,
                                    description: `During the export process, we generated a checksum that currently does not align with the existing group structure data. This discrepancy might be a result of modifications to the data or a subsequent import operation that altered the data.\n\nConsequently, the current import file might potentially become a source for data duplication. Therefore, we recommend regenerating the export file and applying your updates to ensure data integrity and prevent duplication.`,
                                    type: EValidationSeverity.error,
                                });
                            }
                        }
                    }
                }

                // Deduplicate validation messages by title to avoid showing the same error multiple times
                const uniqueValidations = new Map<string, IValidationMessage>();
                saveModel.validation.validationMessages.forEach(msg => {
                    if (!uniqueValidations.has(msg.title)) {
                        uniqueValidations.set(msg.title, msg);
                    }
                });
                saveModel.validation.validationMessages = Array.from(uniqueValidations.values());

                // validation rules
                if (saveModel.validation.validationMessages.length > 0) {
                    saveModel.validation.state = EValidationState.PendingUser;
                }
                else {
                    saveModel.validation.state = EValidationState.Fulfilled;
                }
            }

            if (saveModel.validation && saveModel.validation.state !== EValidationState.Fulfilled) {
                throw new Error("Import paused, due to validation, user input required..");
            }

            dispatch(addUpdateGroupStructure({
                groupNumbers: saveModel.groupNumbers,
                cesNumbers: saveModel.cesNumbers,
                departments: saveModel.departments,
            }));


            _logs.push({
                date: new Date(),
                title: `Import complete.`,
                type: EValidationSeverity.info,
            });

            setFileUpload(null);

            setViewModel(t => ({ ...t, view: EViewState.results }));

            saveModel = null;

        } catch (error: any) {
            _logs.push({
                date: new Date(),
                title: error?.message,
                type: EValidationSeverity.warn,
            });
        } finally {

            // Deduplicate logs by title before displaying
            const allLogs = [...saveModelLogs, ..._logs];
            const uniqueLogs = new Map<string, IValidationMessage>();
            allLogs.forEach(log => {
                if (!uniqueLogs.has(log.title)) {
                    uniqueLogs.set(log.title, log);
                }
            });

            setPrompts(t => ([...t, ...Array.from(uniqueLogs.values())]));
            setUpdateTransaction(saveModel);

        }
    }

    const onRenderValidationDialog = () => {
        if (updateTransaction?.validation?.state === EValidationState.PendingUser)
            return <ValidationDialog
                defaultValue={updateTransaction?.validation}
                onCancel={(xValidationModel) => {
                    setUpdateTransaction(null);
                }}
                onContinue={(xValidationModel) => {
                    const nSaveTransaction: ISaveTransaction = {
                        ...updateTransaction!,
                        validation: {
                            ...updateTransaction.validation!,
                            state: EValidationState.Fulfilled
                        }
                    }
                    updateGroupConfig(nSaveTransaction);
                }}
            />
    }

    const onRenderBody = () => {
        let onRender: (() => JSX.Element) | null = null;

        switch (viewModel.view) {
            case EViewState.export:
                onRender = onRenderImportExport;
                break;

            case EViewState.import:
                onRender = onRenderImportExport;
                break;

            case EViewState.results:
                onRender = onRenderResults;
                break;
        }

        if (onRender == null)
            throw new Error("unexpected view state.");

        return <>
            {onRender()}
            {onRenderValidationDialog()}
        </>
    }

    const onRenderFooter = () => {

        switch (viewModel.view) {
            case EViewState.export:
                return onRenderExportFooter();

            case EViewState.import:
                return onRenderImportFooter();

            case EViewState.results:
                return onRenderResultsFooter();

            default:
                throw new Error("unexpected view state.");
        }
    }

    return <Panel
        isOpen={props.isOpen}
        onDismiss={props.onCancel}
        type={PanelType.medium}
        layerProps={{ eventBubblingEnabled: true }}
        headerText={viewModel.view === EViewState.export ? (props.isReport ? "Group Structure Report" : "Group Structure Export") : "Group Structure Import"}
        onRenderFooterContent={onRenderFooter}
        isFooterAtBottom={true}
    >
        {onRenderBody()}
    </Panel>
}

export default GroupStructureImportWizard;